<template>
  <div class="animated fadeIn">
    <instrument-runner-sample-filter
      :samples_loading="samples_loading"
      :collection="task_collection"
      @updateSampleFilters="updateSampleFilters"
    />
    <div v-if="samples_loading" class="text-center my-4 font-2xl text-muted">
      <i class="fal fa-spinner fa-spin" /> Samples Data Loading...
    </div>
    <template v-else>
      <basic-heading title="Scheduled Samples" class="mb-3" :size="3">
        <template #right-content>
          <span>Samples: {{ table.total_rows }}</span>
          <span class="ml-2">Batches: </span>
          <span class="ml-2">Results: </span>
          <span class="ml-2">Jig: </span>
        </template>
      </basic-heading>
      <template v-if="sample_collection.length">
        <b-table
          :items="sample_collection"
          :fields="table.fields"
          v-sortable="table.sorts"
          responsive
        >
          <template #cell(index)="data">
            {{ data.item.jig_index }}
          </template>
          <template #cell(sample)="data">
            <existence-gate
              :entity="data.item"
              :levels="['sample']"
              :field="'pid'"
            >
              <template #existing-content>
                <router-link
                  :to="`/samples/${getData(data.item, {
                    levels: ['sample'],
                    field: 'id',
                  })}`"
                >
                  {{ data.item.sample.pid }}
                </router-link>
              </template>
            </existence-gate>
          </template>
          <template #cell(receiving_id)="data">
            <existence-gate
              :entity="data.item"
              :levels="['sample']"
              :field="'receiving_id'"
            >
              <template #existing-content>
                <template v-if="data.item.sample.receiving_id">
                  {{ data.item.sample.receiving_id }}
                </template>
                <a v-else class="btn btn-secondary disabled px-1 py-0">
                  Not Received
                </a>
              </template>
            </existence-gate>
          </template>
          <template #cell(customer)="data">
            <existence-gate
              :entity="data.item"
              :levels="table_data.customer"
              :field="'name'"
            >
              <template #existing-content>
                <router-link
                  :to="`/customer/${getData(data.item, {
                    levels: table_data.customer,
                    field: 'id',
                  })}`"
                >
                  {{
                    getData(data.item, {
                      levels: table_data.customer,
                      field: "name",
                    })
                  }}
                </router-link>
              </template>
            </existence-gate>
          </template>
          <template #cell(submission_pid)="data">
            <existence-gate
              :entity="data.item"
              :levels="table_data.submission"
              :field="'pid'"
            >
              <template #existing-content>
                <router-link
                  :to="`/submissions/${getData(data.item, {
                    levels: table_data.submission,
                    field: 'id',
                  })}`"
                >
                  {{
                    data.item.sample.submission.pid
                  }}
                </router-link>
              </template>
            </existence-gate>
          </template>
        </b-table>
        <custom-table-pagination
          :current_page="table.current_page"
          :per_page="table.per_page"
          :total_rows="table.total_rows"
          @update="updatePaginationData"
        />
      </template>
      <b-alert v-else show class="py-1 m-0">
        <i class="fa fa-exclamation-circle" /> No samples found.
      </b-alert>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import Multiselect from "vue-multiselect";
import BasicHeading from "@/components/shared/BasicHeading";
import CustomTablePagination from "@/components/shared/CustomTablePagination";
import ExistenceGate from "@/components/shared/ExistenceGate";
import InstrumentRunnerSampleFilter from "./InstrumentRunnerSampleFilter";
import sortable from "@/components/shared/directives/Sortable";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { getEntityData, generatePid } from "@/shared/utils";

const InstrumentInstanceRepository =
  LabRepositoryFactory.get("instrumentInstance");

export default {
  name: "InstrumentRunnerSampleTable",
  components: {
    BasicHeading,
    CustomTablePagination,
    ExistenceGate,
    InstrumentRunnerSampleFilter,
    Multiselect,
  },
  directives: { sortable },
  props: {
    instance: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: true,
      samples_loading: true,
      sample_collection: [],
      filters: {
        process_type: null,
        sample_type: null,
        process: null,
        tasks: [],
      },
      task_collection: null,
      table: {
        fields: [
          { key: "batch_index", label: "Batch" },
          { key: "index", label: "Index" },
          { key: "sample", label: "Sample" },
          { key: "receiving_id", label: "RID" },
          { key: "customer", label: "Customer" },
          { key: "submission_pid", label: "Submission" },
          { key: "result", label: "Result" },
        ],
        current_page: 1,
        per_page: 100,
        total_rows: 50,
        sorts: null,
      },
      table_data: {
        customer: ["sample", "submission", "project", "customer"],
        submission: ["sample", "submission"],
      },
    };
  },
  methods: {
    generatePid({ entity, prefix }) {
      return generatePid({ entity, prefix });
    },
    getData(entity, data) {
      return getEntityData({
        entity,
        ...data,
      });
    },
    updateTableData(table_data, total) {
      this.table.total_rows = total;
      this.sample_collection = table_data;
    },
    /**
     * Emit filter value changes.
     */
    emitFilterExportData() {
      const { process_type, sample_type, process, tasks } = this.filters;

      this.$emit("updateExportData", {
        field: "schedulable_process_type",
        value: process_type ? process_type : null,
      });

      this.$emit("updateExportData", {
        field: "schedulable_process_id",
        value: process ? process : null,
      });

      this.$emit("updateExportData", {
        field: "sample_type_id",
        value: sample_type ? sample_type : null,
      });

      this.$emit("updateExportData", {
        field: "tasks",
        value: tasks ? tasks : null,
      });
    },
    updatePaginationData({ value }) {
      this.table.current_page = value;
      this.fetchSamples();
    },
    async fetchTasks() {
      const { data } = await InstrumentInstanceRepository.getTasks({
        id: this.instance.id,
        limit: "all",
        date: moment().format("YYYY-MM-DD"),
      });

      this.task_collection = data;
    },
    updateSampleFilters(value) {
      this.filters = { ...value };
      this.table.current_page = 1;
      this.emitFilterExportData();
      this.fetchSamples();
    },
    async fetchSamples() {
      const { process_type, sample_type, process, tasks } = this.filters;

      this.samples_loading = true;

      const filters = [
        process_type && {
          field: "process_schedule.schedulable_process_type",
          value: [process_type],
          operator: "ct",
        },
        sample_type && {
          field: "process_schedule.schedule.sample_type_id",
          value: [sample_type],
        },
        process && {
          field: "process_schedule.schedulable_process_id",
          value: [process],
        },
        tasks.length && {
          field: "schedulable_task_id",
          value: tasks,
          operator: "in",
        },
      ].filter((filter) => filter);

      const { per_page, current_page } = this.table;

      const { data } = await InstrumentInstanceRepository.getSamples({
        id: this.instance.id,
        limit: per_page,
        page: current_page,
        filters,
        connections: ["sample.submission.project.customer"],
        date: moment().format("YYYY-MM-DD"),
      });
      this.updateTableData(data.data, data.total);

      this.samples_loading = false;
    },
    async init() {
      this.samples_loading = true;

      await this.fetchTasks();
      await this.fetchSamples();
    },
  },
  created() {
    this.init();
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css" />
